import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { DataTable } from '@/components/ui/data-table';
import { TABLE_DEFAULT_PAGE_SIZE } from '@constants/table';
import type { PaginationState } from '@tanstack/react-table';
import { paginationIntent } from '@utils/tablePagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBreakpointBelow } from '@hooks/useMediaQuery';
import { MOBILE_BREAKPOINT } from '@constants/layout';
import type { DocumentFilters, DocumentListItem, DocumentSortBy } from '@hooks/useDocumentsQuery';
import { buildDocumentColumns } from './documentColumns';
import { filterDocuments } from './documentFilters';
import DocumentTagsDialog from './DocumentTagsDialog';

interface DocumentListViewProps {
  documents?: DocumentListItem[];
  loading?: boolean;
  sortBy?: DocumentSortBy;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: DocumentSortBy) => void;
  onDocumentClick?: (documentId: string) => void;
  searchQuery?: string;
  filters?: DocumentFilters;
  /** 1-based, as the API numbers them. */
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  totalCount?: number;
  onDelete?: (documentId: string) => void;
  canDeleteDocument?: (document: DocumentListItem) => boolean;
  onUpdateTags?: (documentId: string, tags: string[]) => void;
}

const DocumentListView: React.FC<DocumentListViewProps> = ({
  documents = [],
  loading = false,
  sortBy = 'date',
  sortOrder = 'desc',
  onSortChange,
  onDocumentClick,
  searchQuery = '',
  filters,
  page = 1,
  pageSize = TABLE_DEFAULT_PAGE_SIZE,
  onPageChange,
  onPageSizeChange,
  totalCount = 0,
  onDelete,
  canDeleteDocument,
  onUpdateTags,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useBreakpointBelow(MOBILE_BREAKPOINT);

  const [shareOpen, setShareOpen] = useState(false);
  const [tagTarget, setTagTarget] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentListItem | null>(null);

  const rows = useMemo(
    () => filterDocuments(documents, searchQuery, filters),
    [documents, searchQuery, filters],
  );

  const columns = useMemo(
    () =>
      buildDocumentColumns({
        t,
        language: i18n.language,
        canSort: Boolean(onSortChange),
        onShare: () => setShareOpen(true),
        onEditTags: onUpdateTags ? setTagTarget : undefined,
        onDelete: onDelete ? setDeleteTarget : undefined,
        canDelete: canDeleteDocument,
      }),
    [t, i18n.language, onUpdateTags, onDelete, canDeleteDocument, onSortChange],
  );

  // The table counts pages from zero and reports both values on every change,
  // so a size change is told apart by comparing it rather than by which control
  // was used. Resetting to the first page on a size change is the hook's job.
  const handlePaginationChange = useCallback(
    (next: PaginationState) => {
      const intent = paginationIntent(next, pageSize);
      if (intent.type === 'size') onPageSizeChange?.(intent.size);
      else onPageChange?.(intent.page);
    },
    [pageSize, onPageChange, onPageSizeChange],
  );

  const openDocument = (doc: DocumentListItem) =>
    onDocumentClick ? onDocumentClick(doc.id) : navigate(`/documents/${doc.id}`);

  return (
    <div
      className="space-y-3"
      data-testid={isMobile ? 'document-list-mobile' : 'document-list-desktop'}
    >
      <DataTable
        manualSorting
        sorting={[{ id: sortBy === 'name' ? 'title' : sortBy === 'date' ? 'updatedAt' : sortBy, desc: sortOrder === 'desc' }]}
        onSortingChange={(next) => {
          const id = next[0]?.id;
          const field = { title: 'name', updatedAt: 'date', category: 'category', size: 'size' }[id];
          if (field) onSortChange?.(field as DocumentSortBy);
        }}
        columns={columns}
        data={rows}
        loading={loading}
        loadingLabel={t('common.loading')}
        paginated
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={handlePaginationChange}
        totalRows={totalCount}
        onRowClick={openDocument}
        getRowId={(doc) => doc.id}
        empty={
          <div className="flex flex-col items-center gap-3">
            <span className="grid size-11 place-items-center rounded-xl bg-muted text-muted-foreground">
              <FileText className="size-5" strokeWidth={1.6} />
            </span>
            <p className="text-sm text-muted-foreground">{t('documents.noDocumentsFound')}</p>
          </div>
        }
      />

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="revy-ui sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('documents.shareDocument')}</DialogTitle>
            <DialogDescription>{t('documents.shareComingSoon')}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <DocumentTagsDialog
        open={tagTarget !== null}
        initialTags={tagTarget?.tags ?? []}
        onOpenChange={(open) => !open && setTagTarget(null)}
        onSave={(tags) => {
          if (tagTarget) onUpdateTags?.(tagTarget.id, tags);
          setTagTarget(null);
        }}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="revy-ui">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('documents.deleteConfirm', {
                name: deleteTarget?.title || deleteTarget?.fileName || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) onDelete?.(deleteTarget.id);
                setDeleteTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DocumentListView;
